import apiFetch from '@wordpress/api-fetch'

import { apiDelete, apiGet, apiPost, apiPut, type RequestOptions } from './index'
import { getConfiguredApiFetch } from './wpApiFetch'
import { resolveServerTotal } from '../utils/prepCounts'
import type { ClockPrepUsageItem } from './showClockPrepUsage'

export type PrepDateBasis = 'created_at' | 'published_at'

export interface PrepCategory {
  id: number
  slug: string
  name: string
}

export interface PrepCategoryWithCount extends PrepCategory {
  count: number
}

export interface PrepItemVersion {
  form_factor?: string | null
  label?: string | null
  style?: string | null
  body?: string | null
}

export interface PrepListItem {
  id: number
  headline: string
  tease: string | null
  preview?: string | null
  categories: PrepCategory[]
  primary_category: PrepCategory | null
  primary_category_id?: number | null
  event_date?: string | null
  published_at: string | null
  published_at_iso_utc?: string | null
  published_at_epoch?: number | null
  created_at?: string | null
  created_at_iso_utc?: string | null
  created_at_epoch?: number | null
  is_in_my_prep: boolean
  short_story_present: boolean
  short_story_preview?: string | null
  short_story_preferred?: string | null
  short_story_neutral?: string | null
  short_preview?: string | null
  recovery?: PrepRecoveryInfo | null
  versions?: PrepItemVersion[]
  image?: {
    media_id?: number
    thumb?: string
    alt?: string
  } | null
}

export type PrepRecoveryMode = 'dismissed' | 'used' | 'dismissed_all'

export interface PrepRecoveryInfo {
  type: PrepRecoveryMode
  event_at: string | null
  event_at_iso_utc?: string | null
  event_at_epoch?: number | null
  used_date?: string | null
}

export interface PrepListResponse {
  items: PrepListItem[]
  next_cursor: string | null
  prev_cursor: string | null
  total: number
  request_id?: string
}

export interface MyPrepListItemsResponse {
  items: PrepListItem[]
  page: number
  per_page: number
  limit?: number
  total: number
  /**
   * @deprecated use `total` instead. TODO: remove count after deprecation window.
   */
  count?: number
  request_id?: string
}

export interface MyPrepListIdsResponse {
  items: number[]
  page?: number
  per_page?: number
  limit?: number
  total: number
  /**
   * @deprecated use `total` instead. TODO: remove count after deprecation window.
   */
  count?: number
  request_id?: string
}

export type MyPrepListResponse = MyPrepListItemsResponse | MyPrepListIdsResponse

export interface PrepCategoriesResponse {
  categories: PrepCategoryWithCount[]
  request_id?: string
}

export interface MyPrepCountResponse {
  count: number
  request_id?: string
}

export interface PrepUsageEntry {
  user_id: number | null
  used_at: string | null
  used_by_display_name?: string | null
}

export interface PrepUsageFlag {
  prep_item_id: number
  used: boolean
  used_at: string | null
  used_by_user_id: number | null
  used_by_display_name?: string | null
  usages?: PrepUsageEntry[]
  latest_used_at?: string | null
  latest_user_id?: number | null
}

export interface MarkPrepUsedResponse {
  station_id: number
  date: string
  flag: PrepUsageFlag
  ordered_ids?: number[]
  total?: number
  removed_from_my_prep?: boolean
  previous_my_prep_position?: number | null
}

export interface UndoPrepUsedResponse {
  station_id: number
  date: string
  undone: boolean
  flag?: PrepUsageFlag | null
  ordered_ids?: number[]
  total?: number
  restored_to_my_prep?: boolean
}

export interface PrepDetailResponse extends PrepListItem {
  medium_story?: string | null
  long_story?: string | null
  short_story_medium?: string | null
  short_story_long?: string | null
  story_medium?: string | null
  story_long?: string | null
  source_url?: string | null
  original_text?: string | null
  published_at?: string | null
  image?: {
    media_id: number
    alt: string
    caption?: string | null
    sizes?: {
      thumb?: string
      medium?: string
      large?: string
      full?: string
    }
  } | null
  request_id?: string
}

export interface RawPrepDashboardCard {
  id?: string
  type?: string
  category_id?: number | null
  categoryId?: number | null
  category_slug?: string | null
  categorySlug?: string | null
  category_name?: string | null
  categoryName?: string | null
  items_per_page?: number | null
  itemsPerPage?: number | null
  collapsed?: boolean
  isCollapsed?: boolean
  page?: number | string | null
  current_page?: number | string | null
  currentPage?: number | string | null
  headline_lines?: number | null
  headlineLines?: number | null
  list_show_images?: boolean | null
  listShowImages?: boolean | null
  column?: number | null
  column_number?: number | null
  column_order?: number | null
  columnOrder?: number | null
}

export interface PrepDashboardCard {
  id: string
  type: 'my_prep' | 'category'
  categoryId?: number
  categorySlug?: string
  categoryName?: string
  itemsPerPage: number
  collapsed: boolean
  page: number
  headlineLines?: 1 | 2 | 3
  listShowImages?: boolean
  column?: number
  columnOrder?: number
}

export interface PrepDashboardSettings {
  enabled: boolean
  columns: number
  cards: PrepDashboardCard[]
}

export interface PrepDashboardQueryCard {
  id: string
  type: 'category' | 'my_prep'
  page: number
  per_page: number
  category_id?: number
}

export interface PrepDashboardSnapshotCard {
  id: string
  type: 'category' | 'my_prep'
  page: number
  per_page: number
  total: number
  item_ids: number[]
  category_id?: number
  category_name?: string
  category_slug?: string
  category_missing?: boolean
}

export interface PrepDashboardSnapshotResponse {
  station_id: number
  age_max_days: number
  date_basis?: PrepDateBasis
  cards: PrepDashboardSnapshotCard[]
  items: PrepListItem[]
  my_prep_ids: number[]
  usage_date: string | null
  usage_flags: PrepUsageFlag[]
  clock_now: string
  clock_usage: ClockPrepUsageItem[]
  request_id?: string
}

export interface PrepDashboardQueryPayload {
  station_id: number
  age_max_days: number
  date_basis: PrepDateBasis
  include_images: boolean
  cards: PrepDashboardQueryCard[]
}

export interface PrepDashboardRevisionResponse {
  station_id: number
  revisions: {
    global: number
    station: number
    user: number
    token: string
  }
  server_at: string
  request_id?: string
}

export interface RawPrepDashboardSettings {
  enabled?: unknown
  columns?: unknown
  columnCount?: unknown
  cards?: RawPrepDashboardCard[] | null
}

export type AddMyPrepResponse = {
  success: true
  item_id: number
  ordered_ids?: number[]
  total?: number
  request_id?: string
}

export interface ManualAddRequest {
  title: string
  text: string
  category_display_name: string
}

export interface ManualAddResponse {
  id: number
  headline: string
  category: { id: number; name: string }
  created_at: string
  event_date: null
  published_at: string
  published_at_iso_utc: string | null
  published_at_epoch: number | null
}

export type RemoveMyPrepResponse = {
  success: true
  item_id: number
  removed: boolean
  ordered_ids?: number[]
  total?: number
  request_id?: string
}

export type ReorderMyPrepResponse = {
  success: boolean
  ordered_ids?: number[]
  request_id?: string
}

export interface PrepSearchParams {
  scope?: 'my' | 'station' | 'all' | 'global'
  categories?: string[]
  logic?: 'or' | 'and'
  q?: string
  limit?: number
  cursor?: string | null
  age_max_days?: number
  date_basis?: PrepDateBasis
  include_dismissed?: boolean
}

export interface PrepRecoveryParams {
  stationId: number
  mode: PrepRecoveryMode
  categories?: string[]
  logic?: 'or' | 'and'
  q?: string
  age_max_days?: number
  page?: number
  limit?: number
}

const buildSearchQuery = (params: PrepSearchParams): string => {
  const search = new URLSearchParams()

  if (params.scope) {
    search.set('scope', params.scope)
  }

  if (params.categories && params.categories.length > 0) {
    params.categories.forEach((slug) => {
      if (!slug) {
        return
      }
      search.append('categories[]', slug)
    })
  }

  if (params.logic) {
    search.set('logic', params.logic)
  }

  if (params.q) {
    search.set('q', params.q)
  }

  if (params.limit) {
    search.set('limit', String(params.limit))
  }

  if (params.cursor) {
    search.set('cursor', params.cursor)
  }

  if (typeof params.age_max_days === 'number' && Number.isFinite(params.age_max_days)) {
    search.set('age_max_days', String(params.age_max_days))
  }

  if (params.date_basis) {
    search.set('date_basis', params.date_basis)
  }

  if (typeof params.include_dismissed === 'boolean') {
    search.set('include_dismissed', params.include_dismissed ? '1' : '0')
  }

  return search.toString()
}

export const searchPrepItems = async (
  params: PrepSearchParams,
  options: RequestOptions = {},
): Promise<PrepListResponse> => {
  const query = buildSearchQuery(params)
  const path = query ? `prep/search?${query}` : 'prep/search'
  return apiGet<PrepListResponse>(path, options)
}

export interface PrepRecoveryResponse {
  items: PrepListItem[]
  categories?: PrepCategoryWithCount[]
  page: number
  per_page: number
  total: number
  request_id?: string
}

export const fetchPrepRecovery = async (
  params: PrepRecoveryParams,
  options: RequestOptions = {},
): Promise<PrepRecoveryResponse> => {
  const search = new URLSearchParams()
  search.set('station_id', String(params.stationId))
  search.set('mode', params.mode)

  if (params.categories && params.categories.length > 0) {
    params.categories.forEach((slug) => {
      if (!slug) {
        return
      }
      search.append('categories[]', slug)
    })
  }

  if (params.logic) {
    search.set('logic', params.logic)
  }

  if (params.q) {
    search.set('q', params.q)
  }

  if (typeof params.age_max_days === 'number' && Number.isFinite(params.age_max_days)) {
    search.set('age_max_days', String(params.age_max_days))
  }

  if (typeof params.page === 'number' && Number.isFinite(params.page)) {
    search.set('page', String(params.page))
  }

  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    search.set('limit', String(params.limit))
  }

  const path = `prep/recovery?${search.toString()}`
  return apiGet<PrepRecoveryResponse>(path, options)
}

export interface ListMyPrepItemsParams {
  page?: number
  limit?: number
  ids_only?: boolean
}

export function listMyPrepItems(
  params: ListMyPrepItemsParams & { ids_only?: false },
  options?: RequestOptions,
): Promise<MyPrepListItemsResponse>
export function listMyPrepItems(
  params: ListMyPrepItemsParams & { ids_only: true },
  options?: RequestOptions,
): Promise<MyPrepListIdsResponse>
export function listMyPrepItems(
  params?: ListMyPrepItemsParams,
  options?: RequestOptions,
): Promise<MyPrepListResponse>
export function listMyPrepItems(
  params: ListMyPrepItemsParams = {},
  options: RequestOptions = {},
): Promise<MyPrepListResponse> {
  const { page, limit, ids_only } = params
  const normalizedLimit =
    typeof limit === 'number' && Number.isFinite(limit) ? Math.max(1, Math.floor(limit)) : undefined

  return getMyPrepList({
    page,
    limit: normalizedLimit,
    ids_only,
    signal: options.signal,
  })
}

export const getPrepUsageFlags = async (
  stationId: number,
  prepIds: number[],
  date?: string,
  options: RequestOptions = {},
): Promise<{ station_id: number; date: string; flags: PrepUsageFlag[] }> => {
  const search = new URLSearchParams()
  search.set('station_id', String(stationId))
  prepIds.forEach((id) => search.append('prep_ids[]', String(id)))
  if (date) {
    search.set('date', date)
  }
  const path = `prep/used-today?${search.toString()}`
  return apiGet<{ station_id: number; date: string; flags: PrepUsageFlag[] }>(path, options)
}

export const markPrepUsed = async (
  stationId: number,
  prepItemId: number,
  date?: string,
  options: RequestOptions = {},
): Promise<MarkPrepUsedResponse> => {
  const payload: Record<string, unknown> = {
    station_id: stationId,
    prep_item_id: prepItemId,
  }
  if (date) {
    payload.date = date
  }
  return apiPost<MarkPrepUsedResponse>('prep/used', payload, options)
}

export const undoPrepUsed = async (
  stationId: number,
  prepItemId: number,
  date?: string,
  restore?: { myPrep: boolean; position: number },
  options: RequestOptions = {},
): Promise<UndoPrepUsedResponse> => {
  const search = new URLSearchParams()
  search.set('station_id', String(stationId))
  search.set('prep_item_id', String(prepItemId))
  if (date) {
    search.set('date', date)
  }
  if (restore?.myPrep) {
    search.set('restore_my_prep', '1')
    search.set('my_prep_position', String(Math.max(0, Math.floor(restore.position))))
  }
  const path = `prep/used?${search.toString()}`
  return apiDelete<UndoPrepUsedResponse>(path, options)
}
export const fetchPrepCategories = async (
  params: {
    scope?: 'global' | 'my'
    q?: string
    ageMaxDays?: number
    dateBasis?: PrepDateBasis
    includeEmpty?: boolean
  },
  options: RequestOptions = {},
): Promise<PrepCategoriesResponse> => {
  const search = new URLSearchParams()
  search.set('scope', params.scope ?? 'global')
  if (params.q) {
    search.set('q', params.q)
  }
  if (typeof params.ageMaxDays === 'number' && Number.isFinite(params.ageMaxDays) && params.ageMaxDays > 0) {
    search.set('age_max_days', String(params.ageMaxDays))
  }
  if (params.dateBasis) {
    search.set('date_basis', params.dateBasis)
  }
  if (typeof params.includeEmpty === 'boolean') {
    search.set('include_empty', params.includeEmpty ? '1' : '0')
  }
  const query = search.toString()
  const path = query ? `prep/categories?${query}` : 'prep/categories'
  return apiGet<PrepCategoriesResponse>(path, options)
}

export interface PrepCategoryResponse {
  category: PrepCategory
  request_id?: string
}

export interface PrepCategoryDeleteResponse {
  deleted: boolean
  category_id: number
  reassigned_count?: number
  request_id?: string
}

export const createPrepCategory = async (
  name: string,
  options: RequestOptions = {},
): Promise<PrepCategoryResponse> => {
  return apiPost<PrepCategoryResponse>('prep/categories', { name }, options)
}

export const updatePrepCategory = async (
  categoryId: number,
  name: string,
  options: RequestOptions = {},
): Promise<PrepCategoryResponse> => {
  return apiPut<PrepCategoryResponse>(`prep/categories/${categoryId}`, { name }, options)
}

export const deletePrepCategory = async (
  categoryId: number,
  replacementCategoryId?: number,
  options: RequestOptions = {},
): Promise<PrepCategoryDeleteResponse> => {
  const search = new URLSearchParams()
  if (typeof replacementCategoryId === 'number' && Number.isFinite(replacementCategoryId)) {
    search.set('replacement_category_id', String(replacementCategoryId))
  }
  const query = search.toString()
  const path = query ? `prep/categories/${categoryId}?${query}` : `prep/categories/${categoryId}`
  return apiDelete<PrepCategoryDeleteResponse>(path, options)
}

export const getMyPrepCount = async (
  options: RequestOptions = {},
): Promise<MyPrepCountResponse> => {
  return apiGet<MyPrepCountResponse>('prep/my/count', options)
}

export const fetchPrepItemDetail = async (
  id: number,
  options: RequestOptions = {},
): Promise<PrepDetailResponse> => {
  return apiGet<PrepDetailResponse>(`prep/items/${id}`, options)
}

const normalizeDashboardCard = (card: RawPrepDashboardCard | null | undefined): PrepDashboardCard | null => {
  if (!card || typeof card !== 'object') {
    return null
  }

  const id = typeof card.id === 'string' && card.id.trim() ? card.id.trim() : undefined
  const typeRaw = typeof card.type === 'string' ? card.type.trim().toLowerCase() : ''
  const type = typeRaw === 'my_prep' || typeRaw === 'category' ? typeRaw : null

  if (!id || !type) {
    return null
  }

  const rawCategory = card.categoryId ?? card.category_id
  const categoryId =
    typeof rawCategory === 'number' && Number.isFinite(rawCategory) && rawCategory > 0 ? rawCategory : undefined
  const rawSlug = card.categorySlug ?? card.category_slug
  const categorySlug =
    typeof rawSlug === 'string' && rawSlug.trim() ? rawSlug.trim() : undefined
  const rawName = card.categoryName ?? card.category_name
  const categoryName =
    typeof rawName === 'string' && rawName.trim() ? rawName.trim() : undefined
  const rawItemsPerPage = card.itemsPerPage ?? card.items_per_page
  const itemsPerPage =
    typeof rawItemsPerPage === 'number' && Number.isFinite(rawItemsPerPage) ? Math.round(rawItemsPerPage) : undefined
  const collapsed = Boolean(card.collapsed ?? card.isCollapsed ?? false)
  const rawPage = card.page ?? card.current_page ?? card.currentPage
  const rawHeadlineLines = card.headlineLines ?? card.headline_lines
  const headlineLines =
    rawHeadlineLines === 1 || rawHeadlineLines === 2 || rawHeadlineLines === 3 ? rawHeadlineLines : undefined
  const rawListShowImages = card.listShowImages ?? card.list_show_images
  const listShowImages = typeof rawListShowImages === 'boolean' ? rawListShowImages : undefined
  const rawColumn = card.column ?? card.column_number
  const column =
    typeof rawColumn === 'number' && Number.isFinite(rawColumn) && rawColumn >= 1 ? Math.floor(rawColumn) : undefined
  const rawColumnOrder = card.column_order ?? card.columnOrder
  const columnOrder =
    typeof rawColumnOrder === 'number' && Number.isFinite(rawColumnOrder) && rawColumnOrder >= 1
      ? Math.floor(rawColumnOrder)
      : undefined
  const page = (() => {
    if (typeof rawPage === 'number' && Number.isFinite(rawPage)) {
      return Math.max(1, Math.floor(rawPage))
    }

    if (typeof rawPage === 'string') {
      const parsed = Number.parseInt(rawPage, 10)
      if (Number.isFinite(parsed)) {
        return Math.max(1, parsed)
      }
    }

    return 1
  })()

  return {
    id,
    type,
    ...(categoryId && type === 'category' ? { categoryId } : {}),
    ...(categorySlug && type === 'category' ? { categorySlug } : {}),
    ...(categoryName && type === 'category' ? { categoryName } : {}),
    itemsPerPage: itemsPerPage ?? 5,
    collapsed,
    page,
    ...(headlineLines ? { headlineLines } : {}),
    ...(listShowImages !== undefined ? { listShowImages } : {}),
    ...(column ? { column } : {}),
    ...(columnOrder ? { columnOrder } : {}),
  }
}

export const normalizePrepDashboardSettings = (
  raw: RawPrepDashboardSettings | null | undefined,
): PrepDashboardSettings => {
  const fallbackCards: PrepDashboardCard[] = [
    {
      id: 'my_prep',
      type: 'my_prep',
      itemsPerPage: 5,
      collapsed: false,
      page: 1,
    },
  ]

  if (!raw || typeof raw !== 'object') {
    return {
      enabled: true,
      columns: 3,
      cards: fallbackCards,
    }
  }

  const enabled = Boolean(raw.enabled ?? true)
  const rawColumns = raw.columns ?? raw.columnCount
  const columns = (() => {
    if (typeof rawColumns === 'number' && Number.isFinite(rawColumns)) {
      return Math.min(4, Math.max(1, Math.round(rawColumns)))
    }

    if (typeof rawColumns === 'string') {
      const parsed = Number.parseInt(rawColumns, 10)
      if (Number.isFinite(parsed)) {
        return Math.min(4, Math.max(1, parsed))
      }
    }

    return 3
  })()

  const rawCards = Array.isArray(raw.cards) ? raw.cards : []
  const cards = rawCards
    .map((card) => normalizeDashboardCard(card))
    .filter((card): card is PrepDashboardCard => card !== null)

  return {
    enabled,
    columns,
    cards: cards.length > 0 ? cards : fallbackCards,
  }
}

export interface GetMyPrepListOptions extends RequestOptions {
  page?: number
  perPage?: number
  per_page?: number
  limit?: number
  idsOnly?: boolean
  ids_only?: boolean
}

export function getMyPrepList(options: GetMyPrepListOptions & { idsOnly?: false }): Promise<MyPrepListItemsResponse>
export function getMyPrepList(options: GetMyPrepListOptions & { idsOnly: true }): Promise<MyPrepListIdsResponse>
export function getMyPrepList(options?: GetMyPrepListOptions): Promise<MyPrepListResponse>
export function getMyPrepList(options: GetMyPrepListOptions = {}): Promise<MyPrepListResponse> {
  const { idsOnly, ids_only, page, perPage, per_page, limit, ...rest } = options
  const search = new URLSearchParams()

  if (idsOnly || ids_only) {
    search.set('ids_only', '1')
  }

  if (typeof page === 'number' && Number.isFinite(page)) {
    search.set('page', String(page))
  }

  const resolvedLimit = (() => {
    if (typeof limit === 'number' && Number.isFinite(limit)) {
      return Math.max(1, Math.floor(limit))
    }
    if (typeof perPage === 'number' && Number.isFinite(perPage)) {
      return Math.max(1, Math.floor(perPage))
    }
    if (typeof per_page === 'number' && Number.isFinite(per_page)) {
      return Math.max(1, Math.floor(per_page))
    }
    return undefined
  })()

  if (typeof resolvedLimit === 'number' && Number.isFinite(resolvedLimit)) {
    search.set('limit', String(resolvedLimit))
  }

  const query = search.toString()
  const path = query ? `prep/my?${query}` : 'prep/my'
  return apiGet<MyPrepListResponse>(path, rest).then((response) => {
    const rawPage = (response as { page?: number | string }).page
    const normalizedPage = (() => {
      if (typeof rawPage === 'number' && Number.isFinite(rawPage)) {
        return Math.max(1, Math.floor(rawPage))
      }
      if (typeof rawPage === 'string') {
        const parsed = Number.parseInt(rawPage, 10)
        if (Number.isFinite(parsed)) {
          return Math.max(1, parsed)
        }
      }
      return 1
    })()

    const rawPerPage = (response as { limit?: number | string; per_page?: number | string; perPage?: number | string }).limit ??
      (response as { per_page?: number | string }).per_page ??
      (response as { perPage?: number | string }).perPage
    const normalizedPerPage = (() => {
      if (typeof rawPerPage === 'number' && Number.isFinite(rawPerPage)) {
        return Math.max(1, Math.floor(rawPerPage))
      }
      if (typeof rawPerPage === 'string') {
        const parsed = Number.parseInt(rawPerPage, 10)
        if (Number.isFinite(parsed)) {
          return Math.max(1, parsed)
        }
      }
      if (typeof resolvedLimit === 'number' && Number.isFinite(resolvedLimit)) {
        return Math.max(1, Math.floor(resolvedLimit))
      }
      return 5
    })()

    const normalizedTotal = resolveServerTotal(response, Array.isArray(response.items) ? response.items : undefined)
    const includeDeprecatedCount = 'count' in response ? { count: normalizedTotal } : {}

    return {
      ...response,
      page: normalizedPage,
      per_page: normalizedPerPage,
      ...(typeof (response as { limit?: number }).limit === 'number'
        ? { limit: Math.max(1, Math.floor((response as { limit: number }).limit)) }
        : {}),
      total: normalizedTotal,
      ...includeDeprecatedCount,
    }
  })
}

export const fetchMyPrepList = getMyPrepList

export async function getAllMyPrepItems(): Promise<{ items: PrepListItem[]; total: number }> {
  const PER_PAGE = 15
  let page = 1
  let total = 0
  const all: PrepListItem[] = []

  for (;;) {
    const response = await getMyPrepList({ page, limit: PER_PAGE, ids_only: false })
    const chunk = Array.isArray(response.items) ? (response.items as PrepListItem[]) : []

    if (page === 1) {
      total =
        typeof response.total === 'number' && Number.isFinite(response.total)
          ? Math.max(0, Math.floor(response.total))
          : chunk.length
    }

    all.push(...chunk)

    if (all.length >= total || chunk.length === 0) {
      break
    }

    page += 1

    if (page > 50) {
      break
    }
  }

  return { items: all, total }
}

export async function getMyPrepIds(): Promise<{ items: number[]; total: number }> {
  let page = 1
  let total = 0
  const all: number[] = []

  for (;;) {
    const response = await getMyPrepList({ page, ids_only: true })
    const chunk = Array.isArray(response.items) ? (response.items as number[]) : []

    if (page === 1) {
      total =
        typeof response.total === 'number' && Number.isFinite(response.total)
          ? Math.max(0, Math.floor(response.total))
          : chunk.length
    }

    all.push(...chunk)

    if (all.length >= total || chunk.length === 0) {
      break
    }

    page += 1

    if (page > 50) {
      break
    }
  }

  const resolvedTotal = Number.isFinite(total) ? total : all.length

  return {
    items: all,
    total: resolvedTotal,
  }
}

export const putMyPrepOrder = async (
  items: number[],
  options: RequestOptions = {},
): Promise<ReorderMyPrepResponse> => {
  return apiPut<ReorderMyPrepResponse>('prep/my/order', { items }, options)
}

export const reorderMyPrep = async (
  items: number[],
  options: RequestOptions = {},
): Promise<ReorderMyPrepResponse> => {
  return putMyPrepOrder(items, options)
}

export const getCategoryItems = async (
  categoryId: number,
  params: {
    page?: number
    perPage?: number
    idsOnly?: boolean
    ageMaxDays?: number
    dateBasis?: PrepDateBasis
  } = {},
  options: RequestOptions = {},
): Promise<{ items: Array<PrepListItem | number>; page: number; per_page: number; total: number; request_id?: string }> => {
  const search = new URLSearchParams()

  if (typeof params.page === 'number' && Number.isFinite(params.page)) {
    search.set('page', String(params.page))
  }

  if (typeof params.perPage === 'number' && Number.isFinite(params.perPage)) {
    search.set('per_page', String(params.perPage))
  }

  if (params.idsOnly) {
    search.set('ids_only', '1')
  }

  if (typeof params.ageMaxDays === 'number' && Number.isFinite(params.ageMaxDays) && params.ageMaxDays > 0) {
    search.set('age_max_days', String(params.ageMaxDays))
  }
  if (params.dateBasis) {
    search.set('date_basis', params.dateBasis)
  }

  const query = search.toString()
  const path = query
    ? `prep/categories/${categoryId}/items?${query}`
    : `prep/categories/${categoryId}/items`

  return apiGet<{ items: Array<PrepListItem | number>; page: number; per_page: number; total: number; request_id?: string }>(
    path,
    options,
  )
}

export const getPrepDashboardSettings = async (options: RequestOptions = {}): Promise<PrepDashboardSettings> => {
  const response = await apiGet<{ prep_dashboard?: RawPrepDashboardSettings }>('user/settings', options)
  return normalizePrepDashboardSettings(response.prep_dashboard)
}

export const updatePrepDashboardSettings = async (
  payload: Partial<PrepDashboardSettings>,
  options: RequestOptions = {},
): Promise<PrepDashboardSettings> => {
  const requestPayload: Record<string, unknown> = {}

  if (payload.enabled !== undefined) {
    requestPayload.enabled = Boolean(payload.enabled)
  }

  if (payload.columns !== undefined) {
    requestPayload.columns = payload.columns
  }

  if (payload.cards !== undefined) {
    requestPayload.cards = payload.cards.map((card) => ({
      id: card.id,
      type: card.type,
      ...(card.categoryId ? { categoryId: card.categoryId } : {}),
      itemsPerPage: card.itemsPerPage,
      collapsed: card.collapsed,
      page: card.page,
    }))
  }

  const response = await apiPut<{ prep_dashboard?: RawPrepDashboardSettings }>(
    'user/settings',
    { prep_dashboard: requestPayload },
    options,
  )

  return normalizePrepDashboardSettings(response.prep_dashboard)
}

export const queryPrepDashboard = async (
  payload: PrepDashboardQueryPayload,
  options: RequestOptions = {},
): Promise<PrepDashboardSnapshotResponse> =>
  apiPost<PrepDashboardSnapshotResponse>('prep/dashboard/query', payload, options)

export const getPrepDashboardRevisions = async (
  stationId: number,
  options: RequestOptions = {},
): Promise<PrepDashboardRevisionResponse> => {
  const params = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<PrepDashboardRevisionResponse>(`prep/revisions?${params.toString()}`, options)
}

export async function postManualPrepAdd(body: ManualAddRequest): Promise<ManualAddResponse> {
  const apiFetch = getConfiguredApiFetch()

  return apiFetch<ManualAddResponse>({
    path: 'prep/manual-add',
    method: 'POST',
    data: body,
  })
}

export const addPrepItemToMyPrep = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<AddMyPrepResponse> => {
  return apiPost<AddMyPrepResponse>('prep/my', { item_id: itemId }, options)
}

export const removePrepItemFromMyPrep = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<RemoveMyPrepResponse> => {
  return apiDelete<RemoveMyPrepResponse>(`prep/my/${itemId}`, options)
}

export const dismissPrepItem = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<{ success: boolean; request_id?: string }> => {
  return apiPost<{ success: boolean; request_id?: string }>('prep/dismissals', { item_id: itemId }, options)
}

export const dismissForAll = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<{ success: boolean; request_id?: string }> => {
  return apiPost<{ success: boolean; request_id?: string }>(`prep/items/${itemId}/dismiss-all`, undefined, options)
}

export const restoreDismissForAll = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<{ success: boolean; request_id?: string }> => {
  return apiDelete<{ success: boolean; request_id?: string }>(`prep/items/${itemId}/dismiss-all`, options)
}

export const undoDismissPrepItem = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<{ success: boolean; request_id?: string }> => {
  return apiDelete<{ success: boolean; request_id?: string }>(`prep/dismissals/${itemId}`, options)
}

export const archivePrepItem = async (
  itemId: number,
  options: RequestOptions = {},
): Promise<{ success: boolean; request_id?: string }> => {
  return apiPut<{ success: boolean; request_id?: string }>(`prep/${itemId}`, { status: 'archived' }, options)
}

export const updatePrepItemCategories = async (
  itemId: number,
  categoryIds: number[],
  options: RequestOptions = {},
): Promise<PrepDetailResponse> => {
  return apiPut<PrepDetailResponse>(`prep/items/${itemId}/categories`, { categories: categoryIds }, options)
}




